<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  NConfigProvider,
  NMessageProvider,
  NDialogProvider,
  NNotificationProvider,
  NLoadingBarProvider,
  darkTheme,
  zhCN,
  dateZhCN,
  enUS,
  dateEnUS,
} from 'naive-ui'
import { useAppStore } from '@/stores/app'
import { darkThemeOverrides, lightThemeOverrides } from '@/theme'
import AppLayout from '@/layouts/AppLayout.vue'

const app = useAppStore()
const route = useRoute()

const naiveTheme = computed(() => (app.theme === 'dark' ? darkTheme : null))
const themeOverrides = computed(() => (app.theme === 'dark' ? darkThemeOverrides : lightThemeOverrides))
const naiveLocale = computed(() => (app.locale === 'en' ? enUS : zhCN))
const naiveDateLocale = computed(() => (app.locale === 'en' ? dateEnUS : dateZhCN))

// Reactive: theme/locale changes → reflect on <html>
watch(
  () => app.theme,
  (t) => document.documentElement.setAttribute('data-theme', t),
  { immediate: true },
)
watch(
  () => app.locale,
  (l) => { document.documentElement.lang = l === 'zh' ? 'zh-CN' : 'en' },
  { immediate: true },
)

// Route → store.page (for body[data-page] selector + breadcrumb)
watch(
  () => route.meta?.pageId,
  (pid) => { if (pid) app.setPage(pid as string) },
  { immediate: true },
)

onMounted(() => {
  document.documentElement.setAttribute('data-theme', app.theme)
  document.documentElement.lang = app.locale === 'zh' ? 'zh-CN' : 'en'
  const pid = route.meta?.pageId as string | undefined
  if (pid) app.setPage(pid)
})
</script>

<template>
  <NConfigProvider
    :theme="naiveTheme"
    :theme-overrides="themeOverrides"
    :locale="naiveLocale"
    :date-locale="naiveDateLocale"
    inline-theme-disabled
  >
    <NLoadingBarProvider>
      <NDialogProvider>
        <NNotificationProvider>
          <NMessageProvider>
            <AppLayout />
          </NMessageProvider>
        </NNotificationProvider>
      </NDialogProvider>
    </NLoadingBarProvider>
  </NConfigProvider>
</template>
